import '../styles/Navbar/Navbar.css';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const researchPapers = [
  { label: 'Whitepapers', path: '/research-papers/whitepapers', status: 'coming-soon' },
  { label: 'Actuarial Insight Briefs', path: '/research-papers/actuarial-insight-briefs', status: 'coming-soon' },
  { label: 'Industry Commentary Reports', path: '/research-papers/industry-commentary', status: 'coming-soon' },
  { label: 'Conference Papers', path: '/research-papers/conference-papers', status: 'coming-soon' },
  { label: 'Technical Notes', path: '/research-papers/technical-notes', status: 'coming-soon' },
  { label: 'Public Dashboards', path: '/research-papers/public-dashboards', status: 'coming-soon' },
  { label: 'Regulatory Response Submissions', path: '/research-papers/regulatory-responses', status: 'coming-soon' },
];

function getPlatform() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua) && !/iPhone/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Web';
}

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [platform, setPlatform] = useState('');
  const [showPlatform, setShowPlatform] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  /* Scroll detection */
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (y / docHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Platform detection */
  useEffect(() => {
    const detected = getPlatform();
    setPlatform(detected);
    const timer = setTimeout(() => setShowPlatform(true), 800);
    const hideTimer = setTimeout(() => setShowPlatform(false), 4000);
    return () => { clearTimeout(timer); clearTimeout(hideTimer); };
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* Close menus on route change */
  useEffect(() => {
    setDropdownOpen(false);
    setMobileOpen(false);
  }, [location]);

  /* Lock body scroll when mobile menu is open */
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (path) => location.pathname === path;
  const isBlogsActive = location.pathname.startsWith('/blogs');
  const isGamesActive = location.pathname.startsWith('/games');
  const isEngineActive = location.pathname.startsWith('/valuation-engine');

  return (
    <>
      {/* Overlay backdrop */}
      <div
        className={`nav-overlay ${mobileOpen ? 'open' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div
          className="nav-progress"
          style={{ width: `${scrollProgress}%` }}
          aria-hidden="true"
        />

        {/* Logo */}
        <Link to="/" className="nav-logo">
          <img src="/Main Logo.png" alt="Actyra" className="logo-img" />
        </Link>

        <button
          className={`nav-mobile-toggle ${mobileOpen ? 'active' : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          <span />
          <span />
          <span />
        </button>

        <div className={`nav-menu ${mobileOpen ? 'open' : ''}`}>
          {/* Mobile menu header */}
          <div className="nav-menu-header">
            <div className="nav-menu-header-text">Menu</div>
            <div className="nav-menu-header-sub">Navigate Actyra</div>
          </div>

          <ul className="nav-links">
            <li className="nav-product-item">
              <Link
                to="/valuation-engine"
                className={`nav-product-link ${isEngineActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
              >
                <span className="nav-product-pulse" aria-hidden="true" />
                Valuation Engine
                <span className="nav-product-badge">New</span>
              </Link>
            </li>
            <li>
              <Link to="/" className={isActive('/') ? 'active' : ''} onClick={() => setMobileOpen(false)}>
                Home
              </Link>
            </li>

            <li className="nav-dropdown" ref={dropdownRef}>
              <button
                className={`nav-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <span>Research Papers</span>
                <svg className="dropdown-chevron" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              <div className={`nav-dropdown-menu ${dropdownOpen ? 'visible' : ''}`}>
                <div className="dropdown-header">
                  <span className="dropdown-label">Research Outputs</span>
                  <span className="dropdown-count">{researchPapers.length} categories</span>
                </div>
                {researchPapers.map((paper) => (
                  <Link
                    key={paper.path}
                    to={paper.path}
                    className={`dropdown-item ${paper.status === 'coming-soon' ? 'coming-soon' : ''}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="dropdown-item-text">{paper.label}</span>
                    {paper.status === 'coming-soon' && (
                      <span className="dropdown-badge">Soon</span>
                    )}
                  </Link>
                ))}
              </div>
            </li>

            <li>
              <Link
                to="/blogs"
                className={isBlogsActive ? 'active' : ''}
                onClick={() => setMobileOpen(false)}
              >
                Blogs
              </Link>
            </li>

            {/* Games — direct link to catalog page */}
            <li>
              <Link
                to="/games"
                className={isGamesActive ? 'active' : ''}
                onClick={() => setMobileOpen(false)}
              >
                Games
              </Link>
            </li>

            <li><a href="/#research" onClick={() => setMobileOpen(false)}>Research</a></li>
            <li><a href="/#advisory" onClick={() => setMobileOpen(false)}>Advisory</a></li>
            <li><a href="/#outputs" onClick={() => setMobileOpen(false)}>Outputs</a></li>
          </ul>

          <Link to="/contact" className="nav-cta" onClick={() => setMobileOpen(false)}>
            <span>Engage Us</span>
          </Link>
        </div>
      </nav>

      <div className={`platform-indicator ${showPlatform ? 'visible' : ''}`} aria-hidden="true">
        <div className="platform-dot" />
        <span className="platform-label">{platform}</span>
      </div>
    </>
  );
}
